import { useState, useRef, useEffect } from "react";

import type {
  ITimePicker,
  TCurrentTime,
  THours,
  TMins,
  TTimeField
} from "../../types/time_picker";
import {
  Clock
} from "@unified-checkout/design-system/icons/index";
import {
  HOURS,
  MINUTES,
  AM_PM
} from "../../utils/timeFormatUtils";

import { Label } from "../Label";
import { Dropdown } from "../Dropdown";
import { DropdownButton } from "../Dropdown/DropdownButton";
import { DropdownMenu } from "../Dropdown/DropdownMenu";

import {
  getRotatedHours,
  getRotatedMinutes,
  handleColumnScroll,
  displayTime,
  PLACEHOLDER,
  DISABLED_HRS,
  setInitialScrollPosition,
  getCurrentTime
} from "./utils";
import styles from "./time-picker.module.scss";


export const TimePicker = ({
  name,
  label,
  defaultValue,
  onChangeHandler,
  disabled,
  horizontalPlacement = "auto",
  verticalPlacement = "auto",
  placeholder = PLACEHOLDER
}: ITimePicker) => {
  const hoursRef = useRef<HTMLDivElement>(null);
  const minutesRef = useRef<HTMLDivElement>(null);

  const [currentTime, setCurrentTime] = useState<TCurrentTime | undefined>(defaultValue);
  const [hoursArray, setHoursArray] = useState<THours[]>([]);
  const [minutesArray, setMinutesArray] = useState<TMins[]>([]);

  useEffect(() => {
    if (hoursArray.length > 0 && minutesArray.length > 0) {
      setInitialScrollPosition(hoursRef, minutesRef);
    }
  }, [hoursArray, minutesArray]);

  useEffect(() => {
    onChangeHandler?.(currentTime);
  }, [currentTime]);


  const handleTimeChange = ({ type, value }: TTimeField) => {
    const { hour: currentHr, minute: currentMin, ampm: currentAmPm } = getCurrentTime();

    setCurrentTime(prev => {
      const { hour, minute, ampm } = prev || {};

      const updatedState = {
        hour: hour || currentHr,
        minute: minute || currentMin,
        ampm: ampm || currentAmPm,
        [type]: value
      } as TCurrentTime;

      return updatedState;
    });
  };

  const isCurrentTimeActive = (type: 'hour' | 'minute' | 'ampm', value: string) => {
    if (currentTime) return currentTime[type] === value;
    return getCurrentTime()[type] === value;
  };

  const handleCellClick = (type: 'hour' | 'minute' | 'ampm', value: string) => {
    if (type === 'hour' && DISABLED_HRS.includes(value as THours)) return;
    if ((type === 'minute' || type === 'ampm') && DISABLED_HRS.includes(currentTime?.hour as any)) return;
    handleTimeChange({ type, value } as TTimeField);
  };

  const isCellDisabled = (type: 'hour' | 'minute' | 'ampm', value: string) => {
    if (type === 'hour') return DISABLED_HRS.includes(value as THours);
    return DISABLED_HRS.includes(currentTime?.hour as any);
  };

  const handleMenuToggle = (open: boolean) => {
    if (open) {
      const { hour: currentHr, minute: currentMin } = getCurrentTime();
      const activeHour = (currentTime?.hour || currentHr) as THours;
      const activeMinute = (currentTime?.minute || currentMin) as TMins;
      setHoursArray(getRotatedHours(activeHour));
      setMinutesArray(getRotatedMinutes(activeMinute));
    }
  };

  const currentTimeText = currentTime ? displayTime(currentTime) : '';


  return (
    <div className={styles["time-picker-wrapper"]}>

      {
        (label?.text || label?.hint) &&
        <Label
          inputName={name}
          text={label?.text}
          hintText={label?.hint}
        />
      }

      <Dropdown>
        <DropdownButton
          type="input"
          text={currentTimeText}
          leadingIcon={Clock}
          disabled={disabled}
          placeholder={placeholder}
        />
        <DropdownMenu
          width="fit-content"
          onToggleHandler={handleMenuToggle}
          horizontalPlacement={horizontalPlacement}
          verticalPlacement={verticalPlacement}
        >
          <div className={styles["time-picker-wrapper__picker__menu"]}>
            <div
              ref={hoursRef}
              className={styles["time-picker-wrapper__picker__menu__column"]}
              onScroll={() => handleColumnScroll(hoursRef, HOURS.length)}
            >
              {hoursArray.map(
                (h, index) =>
                  <div
                    key={`time-picker-hour-${index}`}
                    className={styles["time-picker-wrapper__picker__menu__column__cell"]}
                    data-time-picker-cell-active={isCurrentTimeActive('hour', h)}
                    data-time-picker-cell-disabled={isCellDisabled('hour', h)}
                    onClick={() => handleCellClick('hour', h)}
                  >
                    {h}
                  </div>
              )}
            </div>
            <div
              ref={minutesRef}
              className={styles["time-picker-wrapper__picker__menu__column"]}
              onScroll={() => handleColumnScroll(minutesRef, MINUTES.length)}
            >
              {minutesArray.map(
                (m, index) =>
                  <div
                    key={`time-picker-minute-${index}`}
                    className={styles["time-picker-wrapper__picker__menu__column__cell"]}
                    data-time-picker-cell-active={isCurrentTimeActive('minute', m)}
                    data-time-picker-cell-disabled={isCellDisabled('minute', m)}
                    onClick={() => handleCellClick('minute', m)}
                  >
                    {m}
                  </div>
              )}
            </div>
            <div className={styles["time-picker-wrapper__picker__menu__column"]}>
              {
                AM_PM.map(
                  (x, index) =>
                    <div
                      key={`time-picker-am-pm:${index}`}
                      className={styles["time-picker-wrapper__picker__menu__column__cell"]}
                      data-time-picker-cell-active={isCurrentTimeActive('ampm', x)}
                      data-time-picker-cell-disabled={isCellDisabled('ampm', x)}
                      onClick={() => handleCellClick('ampm', x)}
                    >
                      {x}
                    </div>
                )
              }
            </div>
          </div>
        </DropdownMenu>
      </Dropdown>
    </div>
  )
}